import React, { useState, useMemo, useEffect, useCallback, useContext, useRef } from "react";
import { Box, Typography, Button, InputLabel, Stack, TextField, Divider, Switch, Tabs, Tab, Autocomplete, Chip, Link } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { EventsStoreContext } from "../../Store/EventsStore";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { addOrUpdateEvent, deleteEvent, notifyUsersAboutEvent, getEventById } from "../../Requests/EventService.ts";
import ImageDropzone from "../StyledComponents/AvatarDropzone/ImageDropzone.tsx";
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { getSpeakers } from "../../Requests/SpeakerService.ts";
import { getUsers } from "../../Requests/UserService.ts";
import { Remove, DeleteForever, NotificationsActive, Add, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportTableToCsv, exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import GooglePlacesAutocomplete, { geocodeByAddress } from "react-google-places-autocomplete";
import { googleAPIKey } from "../../Constants";
import { getEventCategories } from "../../Requests/EventCategoriesService.ts";
import { SpeakerModel } from "../../Models/Speaker.ts";
import { MemberModel } from "../../Models/Member.ts";
import { EventCategoriesModel } from "../../Models/EventCategoriesModel.ts";
import { Event, EventModel } from "../../Models/Event.ts";
import { GuestModel } from "../../Models/Guest.ts";
import { getGuests } from "../../Requests/GuestService.ts";
import { SERVER_PATH } from "../../Requests/ParseConfiguration.js";
import { EVENT_REGISTRATION_STATUS_CASES, EVENT_REGISTRATION_STATUS } from "../../Models/StateConstants.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView.jsx";
import HtmlEditor from "../HtmlEditorView/HtmlEditor.jsx";

export default function EventInfo() {

  const navigate = useNavigate()
  const { eventid } = useParams()
  const store = useContext(EventsStoreContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [image, setImage] = useState<string | undefined>("")
  const [title, setTitle] = useState<string | undefined>("")
  const [venue, setVenue] = useState<string | undefined>("")
  const [speakers, setSpeakers] = useState<SpeakerModel[]>([])
  const [capacity, setCapacity] = useState<string | undefined>("")
  const [members, setMembers] = useState<MemberModel[]>([])
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [startTime, setStartTime] = useState<Date>()
  const [endTime, setEndTime] = useState<Date>()
  const [about, setAbout] = useState<string | undefined>("")
  const [street, setStreet] = useState<string | undefined>("")
  const [city, setCity] = useState<string | undefined>("")
  const [postalCode, setPostalCode] = useState<string | undefined>("")
  const [hidden, setHidden] = useState<boolean | undefined>(false)
  const [isNew, setIsNew] = useState<boolean | undefined>(false)
  const [location, setLocation] = useState<any | undefined>({})
  const [eventCategories, setEventCategories] = useState<string[]>([])
  const [interestedMembers, setInterestedMembers] = useState<MemberModel[]>([])
  const [guests, setGuest] = useState<GuestModel[]>([])
  const [registrationStatus, setRegistrationStatus] = useState<{ label: string } | undefined>({ label: "" })
  const [registrationOpenDate, setRegistrationOpenDate] = useState<Date>()
  const [registrationOpenTime, setRegistrationOpenTime] = useState<Date>()
  const [createdAt, setCreatedAt] = useState<Date | undefined>()
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>()
  const [newEventsOrder, setNewEventsOrder] = useState<number | undefined>()

  const [tableData, setTableData] = useState<MemberModel[]>([])
  const [selectedChip, setSelectedChip] = useState<number>(0)
  useEffect(() => {
    selectedChip === 0 ? setTableData(members) : setTableData(interestedMembers)
  }, [selectedChip, members, interestedMembers])

  const columns = useMemo(() => [
    {
      accessorFn: (originalRow) => originalRow.name + " " + originalRow.surname,
      header: 'Member',
      Cell: ({ renderedCellValue, row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          {selectedChip === 0 ? <Remove /> : <Add />}
          <img
            alt="avatar"
            height={32}
            width={32}
            src={row.original.avatar}
            loading="lazy"
            style={{ borderRadius: '50%' }}
          />
          <span>{renderedCellValue}</span>
        </Box>
      )
    }
  ], [selectedChip])
  const data = useMemo(() => tableData, [tableData])
  const [allSpeakers, setAllspeakers] = useState<SpeakerModel[]>([])
  const [allmembers, setAllmembers] = useState<MemberModel[]>([])
  const [allEventCategories, setAllEventCategories] = useState<string[]>([])
  const [allGuests, setAllGuests] = useState<GuestModel[]>([])

  const table = useMaterialReactTable({
    columns,
    data,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableColumnActions: false,
    enableSorting: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handeClickRow(row.original),
      sx: {
        cursor: 'pointer'
      }
    }),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      }
    }
  })

  const [selectedTab, setSelectedTab] = useState(0)
  const handleSelectedTab = (event, newValue) => {
    setSelectedTab(newValue)
  }

  function convertToDate(date?: Date, time?: Date): Date {
    const t = dayjs(time)
    return dayjs(date).hour(t.hour()).minute(t.minute()).toDate()
  }

  const handleSaveChanges = async () => {
    let event = new Event()
    event.objectId = eventid
    event.title = title
    event.venue = venue
    event.speakers = speakers
    event.capacity = capacity
    event.startDate = convertToDate(startDate, startTime)
    event.endDate = convertToDate(endDate, endTime)
    event.about = about
    event.street = street
    event.city = city
    event.postalCode = postalCode
    event.hidden = hidden
    event.isNew = isNew
    event.location = location
    event.members = members
    event.eventCategories = eventCategories
    event.interestedMembers = interestedMembers
    event.guests = guests
    event.registrationStatus = registrationStatus?.label
    event.registrationStartDate = convertToDate(registrationOpenDate, registrationOpenTime)
    event.newEventsOrder = newEventsOrder
    await addOrUpdateEvent(event, image)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/events")
  }

  const googleAddressChange = async (event) => {
    geocodeByAddress(event.label)
      .then(place => {
        let lat = place[0].geometry.location.lat()
        let lng = place[0].geometry.location.lng()
        setLocation({ latitude: lat, longitude: lng })

        for (var i = 0; i < place[0].address_components.length; i++) {
          var addressType = place[0].address_components[i].types[0]
          switch (addressType) {
            case 'postal_town':
              setCity(place[0].address_components[i].short_name)
              break
            case 'route':
              setStreet(place[0].address_components[i].short_name)
              break
            case 'postal_code':
              setPostalCode(place[0].address_components[i].short_name)
              break
            default:
              break
          }
        }
      })
  }

  const handleNotification = async () => {
    notifyUsersAboutEvent(eventid, title)
  }

  const handleDeleteEvent = async () => {
    if (!eventid) return
    await deleteEvent(eventid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/events")
  }

  const handleAddMember = async (member) => {
    if (member === null) {
      return
    }
    let newMembersArr = [...members, member]
    setMembers(newMembersArr.sort((a, b) => a.name.localeCompare(b.name)))
  }

  const handeClickRow = async (member: MemberModel) => {
    switch (selectedChip) {
      case 0:
        handeleDeleteMember(member)
        break
      case 1:
        handeleAddMember(member)
        break
    }
  }

  const handeleDeleteMember = async (member: MemberModel) => {
    const newMembersArr: MemberModel[] = members?.filter((value: MemberModel) => !value.objectId?.includes((member.objectId ?? "")))
    setMembers(sortMembers(newMembersArr))
  }

  const handeleAddMember = async (member: MemberModel) => {
    const newMembersArr: MemberModel[] = [...members, member]
    setMembers(sortMembers(newMembersArr))
    const newInterestedMembersArr: MemberModel[] = interestedMembers?.filter((value: MemberModel) => !value.objectId?.includes((member.objectId ?? "")))
    setInterestedMembers(sortMembers(newInterestedMembersArr))
  }

  const sortMembers = (members: MemberModel[]): MemberModel[] => {
    return members.sort((a: MemberModel, b: MemberModel) => (a.name ?? "").localeCompare((b.name ?? "")))
  }

  const fetchData = useCallback(async () => {
    if (!eventid) {
      setIsLoading(false)
      return
    }

    const event: EventModel = await getEventById(eventid)

    const speakers: SpeakerModel[] = await getSpeakers()
    const addedSpearestIds = event.speakers?.map((speaker) => speaker.objectId)
    let filtredSpeakers: SpeakerModel[] = []
    for (let index in speakers) {
      if (!addedSpearestIds?.includes(speakers[index].objectId)) {
        filtredSpeakers.push(speakers[index])
      }
    }
    setAllspeakers(filtredSpeakers)

    const users = await getUsers()
    const existIds = event.members?.map((member: MemberModel) => member.objectId)
    const filtredResult = users.filter((member) => !existIds?.includes(member.objectId))
    setAllmembers(filtredResult)

    const categories: EventCategoriesModel[] = await getEventCategories()
    setAllEventCategories(categories.map((category: EventCategoriesModel) => (category.name ?? "")))

    const guests: GuestModel[] = await getGuests()
    setAllGuests(guests)

    setImage(event.image)
    setTitle(event.title)
    setVenue(event.venue)
    setSpeakers(event.speakers)
    setCapacity(event.capacity)
    setMembers(event.members.sort((a: MemberModel, b: MemberModel) => (a.name ?? "").localeCompare((b.name ?? ""))))
    setStartDate(event.startDate)
    setEndDate(event.endDate)
    setStartTime(event.startDate)
    setEndTime(event.endDate)
    setAbout(event.about)
    setStreet(event.street)
    setCity(event.city)
    setPostalCode(event.postalCode)
    setHidden(event.hidden)
    setIsNew(event.isNew)
    setLocation(event.location)
    setEventCategories(event.eventCategories)
    setInterestedMembers(event.interestedMembers.sort((a: MemberModel, b: MemberModel) => (a.name ?? "").localeCompare((b.name ?? ""))))
    setGuest(event.guests)
    setRegistrationStatus({ label: event.registrationStatus ?? "" })
    setRegistrationOpenDate(event.registrationStartDate)
    setRegistrationOpenTime(event.registrationStartDate)
    setCreatedAt(event.createdAt)
    setUpdatedAt(event.updatedAt)
    setNewEventsOrder(event.newEventsOrder)

    setIsLoading(false)
  }, [eventid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getEventForExport = () => ({
    objectId: eventid,
    image,
    title,
    venue,
    speakers,
    capacity,
    startDate,
    endDate,
    startTime,
    endTime,
    about,
    street,
    city,
    postalCode,
    hidden,
    isNew,
    location,
    eventCategories,
    registrationStatus: registrationStatus?.label,
    registrationOpenDate,
    registrationOpenTime,
    createdAt,
    updatedAt,
    newEventsOrder,
    membersCount: members?.length,
    interestedCount: interestedMembers?.length,
    guestsCount: guests?.length,
  })

  const loaderView = <CircularProgressView />

  const APP_BAR_HEIGHT = 64
  const headerRef = useRef<HTMLDivElement | null>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const contentView = <>
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/events")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {title}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
      <Box
        sx={{
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleSelectedTab}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Event Details" />
          <Tab label="Registration" />
          <Tab label="Members" />
        </Tabs>
        <Divider />
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <Box
          width="600px"
          hidden={selectedTab !== 0}
        >
          <ImageDropzone
            title={"Event Image"}
            imageUrl={image ?? null}
            callbackFile={(file) => { setImage(file ?? undefined) }}
          />

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Title"}</InputLabel>
            <TextField
              placeholder="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Venue"}</InputLabel>
            <TextField
              placeholder="Venue"
              value={venue}
              onChange={(event) => setVenue(event.target.value)}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Event Category"}</InputLabel>
            <Autocomplete
              multiple
              options={allEventCategories}
              getOptionLabel={(value) => value}
              filterSelectedOptions
              defaultValue={eventCategories}
              onChange={(event, value) => setEventCategories(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Category"
                />
              )}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Speakers"}</InputLabel>
            <Autocomplete
              multiple
              options={allSpeakers}
              getOptionLabel={(option) => option.firstName + " " + option.lastName}
              filterSelectedOptions
              defaultValue={speakers}
              onChange={(event, value) => setSpeakers(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Speakers"
                />
              )}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Guests"}</InputLabel>
            <Autocomplete
              multiple
              options={allGuests}
              getOptionLabel={(option) => option.firstName + " " + option.lastName}
              filterSelectedOptions
              defaultValue={guests}
              onChange={(event, value) => setGuest(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Guests"
                />
              )}
            />
          </Stack>

          <Box
            sx={{
              display: "flex",
              gap: 1
            }}
          >
            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Start Date"}</InputLabel>
              <DatePicker
                format="DD/MM/YYYY"
                value={dayjs(startDate)}
                onChange={(newValue) => setStartDate(newValue?.toDate())}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"End Date"}</InputLabel>
              <DatePicker
                format="DD/MM/YYYY"
                value={dayjs(endDate)}
                onChange={(newValue) => setEndDate(newValue?.toDate())}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1
            }}
          >
            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Start Time"}</InputLabel>
              <TimePicker
                ampm={false}
                value={dayjs(startTime)}
                onChange={(newValue) => setStartTime(newValue?.toDate())}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"End Time"}</InputLabel>
              <TimePicker
                ampm={false}
                value={dayjs(endTime)}
                onChange={(newValue) => setEndTime(newValue?.toDate())}
              />
            </Stack>
          </Box>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"About"}</InputLabel>
            <HtmlEditor
              initialValue={about}
              onChange={value => setAbout(value)} />
          </Stack>

          <Stack
            spacing={1}
            marginTop={8}
          >
            <InputLabel>{"Search Address"}</InputLabel>
            <GooglePlacesAutocomplete
              apiKey={googleAPIKey}
              selectProps={{
                onChange: googleAddressChange,
              }}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Street"}</InputLabel>
            <TextField
              placeholder="Street"
              value={street}
              onChange={(event) => setStreet(event.target.value)}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"City"}</InputLabel>
            <TextField
              placeholder="City"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Postal Code"}</InputLabel>
            <TextField
              placeholder="Postal Code"
              value={postalCode}
              onChange={(event) => setPostalCode(event.target.value)}
            />
          </Stack>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Sort order"}</InputLabel>
            <TextField
              type="number"
              placeholder="Enter event position"
              value={newEventsOrder}
              onChange={(event) => setNewEventsOrder(Number(event.target.value))}
            />
          </Stack>
        </Box>

        <Box
          width="600px"
          hidden={selectedTab !== 1}
        >
          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Registration Status"}</InputLabel>
            <Autocomplete
              options={EVENT_REGISTRATION_STATUS_CASES}
              renderInput={(params) => <TextField {...params} />}
              value={registrationStatus}
              onChange={(_event, value) => setRegistrationStatus(value ?? { label: "" })}
            />
          </Stack>

          <Box
            hidden={registrationStatus?.label !== EVENT_REGISTRATION_STATUS.SCHEDULED}
          >
            <Stack
              spacing={1}
              marginTop={4}
              direction={"row"}
            >
              <Stack
                spacing={1}
                marginTop={4}
              >
                <InputLabel>{"Registration Open Date"}</InputLabel>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={dayjs(registrationOpenDate)}
                  onChange={(newValue) => setRegistrationOpenDate(newValue?.toDate())}
                />
              </Stack>

              <Stack
                spacing={1}
                marginTop={4}
              >
                <InputLabel>{"Registration Open Time"}</InputLabel>
                <TimePicker
                  ampm={false}
                  value={dayjs(registrationOpenTime)}
                  onChange={(newValue) => setRegistrationOpenTime(newValue?.toDate())}
                />
              </Stack>
            </Stack>
          </Box>

          <Stack
            spacing={1}
            marginTop={4}
          >
            <InputLabel>{"Capacity"}</InputLabel>
            <TextField
              placeholder="Capacity"
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
            />
          </Stack>
        </Box>

        <Box
          marginTop={4}
          width="100%"
          hidden={selectedTab !== 2}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1
              }}
            >
              <Chip
                variant={selectedChip === 0 ? "filled" : "outlined"}
                label={"Registered " + members.length}
                onClick={() => {
                  setSelectedChip(0)
                }}
              />
              <Chip
                variant={selectedChip === 1 ? "filled" : "outlined"}
                label={"Interested " + interestedMembers.length}
                onClick={() => {
                  setSelectedChip(1)
                }}
              />
            </Box>

            <Autocomplete
              sx={{
                width: "400px"
              }}
              hidden={selectedChip === 1}
              options={allmembers}
              getOptionLabel={(option) => option.name + " " + option.surname}
              onChange={(event, value) => handleAddMember(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add Members"
                />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              startIcon={<FileDownload />}
              onClick={() => {
                const done = exportTableToCsv(table, eventid ? `event-${eventid}-members` : "event-members");
                if (!done) alert("No data to export.");
              }}
            >
              Export CSV
            </Button>
          </Box>

          <Box
            marginTop={4}
          >
            <MaterialReactTable
              table={table}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "fixed",
          right: 0,
          top: APP_BAR_HEIGHT + 1,
          bottom: 0,
          overflowY: "auto",
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "grey.200",
          zIndex: 110
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() =>
              exportDataToCsv([getEventForExport()], eventid ? `event-${eventid}` : "event", {
                preferredKeys: ["title"],
              })
            }
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Reg. Members"}</InputLabel>
            <InputLabel>
              {
                members.length + "/" + capacity
              }
            </InputLabel>
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputLabel>{"Visibility"}</InputLabel>
            <Switch
              checked={!hidden}
              onChange={(event) => setHidden(!event.target.checked)}
            />
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputLabel>{"Whats's New"}</InputLabel>
            <Switch
              checked={isNew}
              onChange={(event) => setIsNew(event.target.checked)}
            />
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Link
              href={`${SERVER_PATH.SERVER_HOST + "/event?eventId=" + eventid}`}
              target="_blank"
              underline="hover"
            >
              {"Event Link"}
            </Link>
          </Box>

          <Divider />

          <Box
            marginTop={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>
          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleNotification()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <NotificationsActive />
                Push Notification
              </Box>
            </Button>
            <Divider />

            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteEvent()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete Event
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
